import type {DeferredAST} from '@github-ui/code-view-types'
import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {deferredASTPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import type {RefInfo} from '@github-ui/repos-types'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import React, {useEffect, useMemo, useState} from 'react'

import {noVirtualizationThreshold} from '../components/blob/BlobContent/Code/CodeLinesNoVirtualization'

export const EmptyDeferredAST: DeferredAST = {
  stylingDirectives: null,
}

const DeferredASTContext = React.createContext(EmptyDeferredAST)

export function DeferredASTProvider({children, stylingDirectives}: React.PropsWithChildren<DeferredAST>) {
  const value = useMemo<DeferredAST>(() => ({stylingDirectives}), [stylingDirectives])
  return <DeferredASTContext value={value}>{children}</DeferredASTContext>
}

export function useDeferredAST() {
  return React.use(DeferredASTContext)
}

function isValidResponse(obj: DeferredAST): obj is DeferredAST {
  return Array.isArray(obj.stylingDirectives)
}

async function fetchDeferredAST(url: string | null): Promise<DeferredAST> {
  if (!url) return EmptyDeferredAST

  const response = await verifiedFetchJSON(url)

  if (response.ok) {
    const data = await response.json()
    if (data && isValidResponse(data)) {
      return data
    }
  }

  return EmptyDeferredAST
}

export function useDeferredASTQuery(
  refInfo: RefInfo | undefined,
  path: string | undefined,
  has404Error: boolean,
  fileLength: number,
): DeferredAST {
  const isNonVirtualized = isFeatureEnabled('react_blob_overlay')
  const repo = useCurrentRepository()
  const url =
    refInfo && path && !has404Error
      ? deferredASTPath({
          repo,
          commitish: refInfo.name,
          path,
        })
      : null

  const enabled = Boolean(url) && fileLength <= noVirtualizationThreshold && isNonVirtualized

  const {data} = useQuery({
    queryKey: ['deferredAST', repo.ownerLogin, repo.name, refInfo?.name, path, url],
    queryFn: () => fetchDeferredAST(url),
    enabled,
    placeholderData: EmptyDeferredAST,
  })

  return enabled ? (data ?? EmptyDeferredAST) : EmptyDeferredAST
}

export function useLoadDeferredAST(
  repo: Repository,
  refInfo: RefInfo,
  path: string,
  has404Error: boolean,
  fileLength: number,
): DeferredAST {
  const [state, setState] = useState<DeferredAST>(EmptyDeferredAST)
  const isNonVirtualized = isFeatureEnabled('react_blob_overlay')
  const url =
    refInfo && !has404Error
      ? deferredASTPath({
          repo,
          commitish: refInfo.name,
          path,
        })
      : null

  useEffect(() => {
    if (!url) return
    if (fileLength > noVirtualizationThreshold || !isNonVirtualized) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(EmptyDeferredAST)
      return
    }
    let cancelled = false
    const update = async () => {
      setState(EmptyDeferredAST)
      const response = await verifiedFetchJSON(url)

      if (cancelled) {
        return
      }

      try {
        if (response.ok) {
          const data = await response.json()
          if (data && isValidResponse(data)) {
            setState(data)
          }
        } else {
          setState(EmptyDeferredAST)
        }
      } catch {
        setState(EmptyDeferredAST)
      }
    }

    update()

    return function cancel() {
      cancelled = true
    }
  }, [url, fileLength, isNonVirtualized])

  return state
}
